import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, Row, Col } from 'antd';
import { ContractComparisonRow } from '..';
import { CollapseExpandIcon, CustomDivider } from '../../../custom_shared/components';
import { InputComponentRegistry } from '../../node_inputs';
import { InputBaseModel } from '../../models';
import { StyleUtils, getVisibleComparisonInputIds } from '../../misc';
import { InputGroupChangeTracker } from '../../change_trackers';
import { useAppStore } from '../../stores';
import ContractComparisonPanelHeader from './ContractComparisonPanelHeader';
import './ContractComparisonPanelRenderer.less';

interface Props {
    panelId: string;
    panelName: string;
    nodeId: string;
    inputIds: string[];
    inputGroupChangeTracker: InputGroupChangeTracker;
    getNewInputById: (inputId: string) => InputBaseModel | undefined;
    getPreviousInputById: (inputId: string) => InputBaseModel | undefined;
}

const ContractComparisonPanelRenderer: React.FC<Props> = ({
    panelId,
    panelName,
    nodeId,
    inputIds,
    inputGroupChangeTracker,
    getNewInputById,
    getPreviousInputById
}) => {
    const { contractComparisonStore } = useAppStore();

    const getChangeSummaryLabel = React.useCallback((count: number) => {
        switch (contractComparisonStore.selectedFilter) {
            case 'withoutChanges':
                return count === 1 ? '1 field' : `${count} fields`;
            case 'added':
                return count === 1 ? '1 added' : `${count} added`;
            case 'removed':
                return count === 1 ? '1 removed' : `${count} removed`;
            case 'modified':
                return count === 1 ? '1 modified' : `${count} modified`;
            case 'unsaved':
                return count === 1 ? '1 unsaved change' : `${count} unsaved changes`;
            default:
                return count === 0 ? 'No changes' : `${count} change${count === 1 ? '' : 's'}`;
        }
    }, [contractComparisonStore.selectedFilter]);

    const InputComponents = React.useMemo(
        () => InputComponentRegistry[nodeId as keyof typeof InputComponentRegistry],
        [nodeId]
    );

    const inputOrder = React.useMemo(() => Object.keys(InputComponents), [InputComponents]);

    const visibleInputIds = React.useMemo(
        () => getVisibleComparisonInputIds(contractComparisonStore.selectedFilter, inputIds, inputGroupChangeTracker),
        [inputIds, inputGroupChangeTracker.changeCounts, inputGroupChangeTracker.changedInputIds, contractComparisonStore.selectedFilter]
    );

    const changeSummaryLabel = React.useMemo(
        () =>
            getChangeSummaryLabel(
                contractComparisonStore.selectedFilter === 'all'
                    ? inputGroupChangeTracker.totalChangeCount
                    : visibleInputIds.length
            ),
        [contractComparisonStore.selectedFilter, getChangeSummaryLabel, inputGroupChangeTracker.totalChangeCount, visibleInputIds.length]
    );

    if (contractComparisonStore.selectedFilter !== 'all' && !visibleInputIds.length) {
        return null;
    }

    return (
        <Collapse bordered={false} expandIcon={CollapseExpandIcon} defaultActiveKey={[panelId]}>
            <Collapse.Panel
                key={panelId}
                className="contract-comparison-panel"
                header={
                    <ContractComparisonPanelHeader
                        panelName={panelName}
                        changeSummaryLabel={changeSummaryLabel}
                    />
                }
            >
                <CustomDivider type="horizontal" />

                <Row className="contract-comparison-header-row" gutter={10}>
                    <Col style={StyleUtils.getNodeColumnStyleByPercentage(40)}>
                        <span className="header-row-text">Previous Contract</span>
                    </Col>
                    <Col style={StyleUtils.getNodeColumnStyleByPercentage(40)}>
                        <span className="header-row-text">New Contract</span>
                    </Col>
                </Row>

                <CustomDivider type="horizontal" />

                {visibleInputIds
                    .slice()
                    .sort((a, b) => inputOrder.indexOf(a) - inputOrder.indexOf(b))
                    .map((inputId, index) => {
                        const component = InputComponents[inputId as keyof typeof InputComponents];

                        if (!component) {
                            console.debug('Component not found for input: ', inputId);
                            return null;
                        }

                        const inputChangeTracker = inputGroupChangeTracker.getInputChangeTracker(inputId);

                        if (!inputChangeTracker) {
                            console.debug('Input change tracker not found for input: ', inputId);
                            return null;
                        }

                        return (
                            <ContractComparisonRow
                                key={inputId}
                                inputChangeTracker={inputChangeTracker}
                                component={component}
                                newInput={getNewInputById(inputId)}
                                previousInput={getPreviousInputById(inputId)}
                                isLast={index === visibleInputIds.length - 1}
                            />
                        );
                    })}
            </Collapse.Panel>
        </Collapse>
    );
};

export default observer(ContractComparisonPanelRenderer);
